import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';

import Button from '../../components/ui/Button';
import FlipbookViewer from './components/FlipbookViewer';
import ThumbnailSidebar from './components/ThumbnailSidebar';
import ViewerToolbar from './components/ViewerToolbar';
import EmbedCodeModal from './components/EmbedCodeModal';
import { catalogService } from '../../services/catalogService';
import { activityService } from '../../services/activityService';
import { analyticsService } from '../../services/analyticsService';
import { useAuth } from '../../contexts/AuthContext';
import ViewerSkeleton from './components/ViewerSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import CatalogMenuDrawer, { hasMappedDescendant } from '../../components/CatalogMenuDrawer';
import CatalogSearchDrawer from '../../components/CatalogSearchDrawer';

const CatalogViewer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const catalogId = searchParams?.get('id');
  const pageParam = searchParams?.get('page');
  const { user, userProfile, isSuperAdmin, activeCompanyId, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const abortControllerRef = useRef(null);
  const historyDebounceRef = useRef(null);

  // Update currentPage when URL page parameter changes
  useEffect(() => {
    if (pageParam) {
      const pageNum = parseInt(pageParam);
      if (!isNaN(pageNum) && pageNum >= 1) {
        setCurrentPage(pageNum);
      }
    } else {
      setCurrentPage(1);
    }
  }, [pageParam]);

  useEffect(() => {
    // Validate catalogId before attempting to load
    if (!authLoading) {
      if (!catalogId || catalogId === 'null' || catalogId === 'undefined') {
        setError('No catalog ID provided');
        setLoading(false);
        return;
      }
      
      // Basic UUID format validation (8-4-4-4-12 hex characters)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(catalogId)) {
        setError('Invalid catalog ID format');
        setLoading(false);
        return;
      }
      
      // CRITICAL FIX: Wait for userProfile to load if user is authenticated
      // This prevents race condition where user is authenticated but profile hasn't loaded yet
      // For draft catalogs, we need both user.id AND userProfile.companyId
      if (user && !userProfile) {
        console.log('[CATALOG_VIEWER] User authenticated but profile not loaded yet, waiting...');
        return; // Wait for userProfile to load
      }
      
      loadCatalog();
    }

    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
      if (historyDebounceRef.current) {
        clearTimeout(historyDebounceRef.current);
        historyDebounceRef.current = null;
      }
    };
  }, [authLoading, user, userProfile, isSuperAdmin, activeCompanyId, catalogId]);

  const loadCatalog = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      
      // BUSINESS RULE: Internal viewer supports BOTH draft and published catalogs
      // Use dedicated method with authentication and company access validation
      // For super_admin: activeCompanyId is used as fallback; ownership check is bypassed
      const companyIdToUse = userProfile?.company_id || activeCompanyId;
      const data = await catalogService?.getCatalogForViewer(
        catalogId,
        user?.id,
        companyIdToUse,
        isSuperAdmin
      );

      if (controller?.signal?.aborted) return;
      
      if (!data) {
        throw new Error('Catalog not found');
      }
      
      setCatalog(data);
      
      // Get company ID from user profile
      const compId = userProfile?.company_id || null;
      setCompanyId(compId);
      
      await catalogService?.incrementViews(catalogId);
      await activityService?.logActivity({
        type: 'view',
        action: 'viewed',
        catalogId: catalogId,
        catalogTitle: data?.title
      });

      // Track catalog view event
      analyticsService?.trackCatalogView(catalogId, data?.title, compId);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error loading catalog:', error);
      
      // CRITICAL: For AUTHENTICATION_REQUIRED, redirect immediately without showing error
      if (error?.message === 'AUTHENTICATION_REQUIRED') {
        // Redirect to login with return URL
        const returnUrl = encodeURIComponent(window?.location?.pathname + window?.location?.search);
        navigate(`/login?returnUrl=${returnUrl}`);
        return; // Exit early, don't set error state
      }
      
      // Provide user-friendly error messages based on error type
      let errorMessage = 'Failed to load catalog';
      
      if (error?.message === 'CATALOG_NOT_FOUND') {
        errorMessage = 'Catalog not found. It may have been deleted or the link is incorrect.';
      } else if (error?.message === 'ACCESS_DENIED') {
        errorMessage = 'You do not have permission to view this catalog. It belongs to a different company.';
      } else if (error?.message === 'CATALOG_ARCHIVED') {
        errorMessage = 'This catalog has been archived and is no longer available for public viewing.';
      } else {
        errorMessage = error?.message || 'Failed to load catalog';
      }
      
      setError(errorMessage);
    } finally {
      if (!controller?.signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const handleHotspotClick = async (hotspot) => {
    if (catalogId) {
      await catalogService?.incrementHotspotClicks(catalogId);
      await activityService?.logActivity({
        type: 'click',
        action: 'clicked hotspot on',
        catalogId: catalogId,
        catalogTitle: catalog?.title
      });
    }

    if (hotspot?.type === 'buy' && hotspot?.url) {
      window.open(hotspot?.url, '_blank');
    } else if (hotspot?.type === 'link' && hotspot?.url) {
      window.open(hotspot?.url, '_blank');
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);

    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    historyDebounceRef.current = setTimeout(() => {
      historyDebounceRef.current = null;
      window.history?.replaceState(null, '', `?id=${catalogId}&page=${newPage}`);
    }, 300);

    // Track page view event
    if (catalogId && companyId) {
      analyticsService?.trackPageView(catalogId, newPage, companyId);
    }
  };

  const handlePageJump = (pageIndex) => {
    handlePageChange(pageIndex);
  };

  const handleShare = () => {
    console.log('GA4 Event: catalog_share', {
      catalog_id: catalog?.id,
      page_number: currentPage + 1
    });
  };

  const handleDownload = () => {
    console.log('GA4 Event: catalog_download', {
      catalog_id: catalog?.id
    });
    alert('Download functionality would trigger PDF download here');
  };

  const handleEmbed = () => {
    setShowEmbedModal(true);
    console.log('GA4 Event: embed_code_viewed', {
      catalog_id: catalog?.id
    });
  };

  if (authLoading || loading) {
    return <ViewerSkeleton />;
  }

  if (error || !catalog) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
        <div className="flex-1 flex items-center justify-center lg:ml-[280px]">
          <ErrorState
            title={error ? 'Failed to Load Catalog' : 'Catalog Not Found'}
            message={error || 'The catalog you are looking for does not exist or has been removed.'}
            onRetry={error ? loadCatalog : null}
            showRetry={!!error}
            retryText="Try Again"
          />
          {!error && (
            <div className="mt-4">
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-[280px]">
        {/* Main Viewer Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Flipbook Viewer */}
          <div className="flex-1 overflow-hidden">
            <FlipbookViewer
              catalog={catalog}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              companyId={companyId}
              pages={catalog?.catalogPages || []}
            />

          </div>

          {/* Thumbnail Sidebar */}
          <ThumbnailSidebar
            catalog={catalog}
            currentPage={currentPage}
            onPageSelect={handlePageJump}
            isOpen={showThumbnails}
            onToggle={() => setShowThumbnails(!showThumbnails)}
          />

        </div>

       {/* Bottom Toolbar */}
        {/*
        <ViewerToolbar
          catalog={catalog}
          currentPage={currentPage}
          totalPages={catalog?.catalogPages?.length}
          onPageJump={handlePageJump}
          onShare={handleShare}
          onDownload={handleDownload}
          onEmbed={handleEmbed}
          onMenu={catalog?.menuTree?.some(hasMappedDescendant) ? () => setIsMenuOpen(true) : null}
          companyLogoUrl={catalog?.company?.logoUrl}
          onSearch={catalog?.catalogPages?.some(p => p?.keywords?.length > 0) ? () => setIsSearchOpen(true) : null}
        />
        */}
      </div>
        {/* Embed Code Modal */}
        {/*
        <EmbedCodeModal
          catalog={catalog}
          isOpen={showEmbedModal}
          onClose={() => setShowEmbedModal(false)}
        />
        */}

      {/* Navigation Menu Drawer */}
      <CatalogMenuDrawer
        menuTree={catalog?.menuTree || []}
        currentPage={currentPage}
        onNavigate={handlePageChange}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        embedMode={false}
      />

      {/* Search Drawer */}
      <CatalogSearchDrawer
        pages={catalog?.catalogPages || []}
        menuTree={catalog?.menuTree || []}
        currentPage={currentPage}
        onNavigate={handlePageChange}
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        embedMode={false}
      />
    </div>);

};

export default CatalogViewer;